import { useState, useEffect, useRef, useMemo, type ReactNode } from "react";
import {
  applyTheme,
  themeTemplates,
  type ThemeTemplateKey,
} from "../../styles";
import { useUserConfig } from "../../hooks/user/useUserConfig";
import { useAuth } from "../auth/Context";
import { ThemeContext } from "./ThemeContext";
import { decodeUserInfo } from "../../utils/user";

const THEME_STORAGE_KEY = "theme_template";

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useAuth();
  const { userConfig } = useUserConfig();

  // Usamos una referencia para bloquear actualizaciones innecesarias durante la navegación
  const isInitialMount = useRef(true);

  const [currentTheme, setCurrentThemeState] = useState<ThemeTemplateKey>(
    () => {
      if (typeof window === "undefined") return "dark_yellow";

      // PRIORIDAD: localStorage (más reciente, se actualiza al guardar) > Token (puede estar desactualizado)
      // 1. Verificar localStorage primero (se actualiza inmediatamente cuando guardas)
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme && savedTheme in themeTemplates) {
        const theme = savedTheme as ThemeTemplateKey;
        applyTheme(themeTemplates[theme].colors);
        return theme;
      }

      // 2. Si no hay en localStorage, intentar el Token (login inicial)
      const rawToken = localStorage.getItem("auth_token");
      if (rawToken) {
        try {
          const info = decodeUserInfo(rawToken);
          if (info.theme && info.theme in themeTemplates) {
            const theme = info.theme as ThemeTemplateKey;
            applyTheme(themeTemplates[theme].colors);
            // Guardar en localStorage para futuras cargas
            localStorage.setItem(THEME_STORAGE_KEY, theme);
            return theme;
          }
        } catch {
          /* Token inválido */
        }
      }

      // 3. Fallback al tema por defecto
      const defaultTheme = "dark_yellow";
      applyTheme(themeTemplates[defaultTheme].colors);
      return defaultTheme;
    },
  );

  // EFECTO ÚNICO DE SINCRONIZACIÓN (Token + Config)
  useEffect(() => {
    // Si no hay token, no forzamos cambios a menos que sea explícito (evita parpadeo en navegación)
    if (!token) {
      if (!isInitialMount.current) {
        // Solo resetear si realmente el usuario cerró sesión
        setCurrentThemeState("dark_yellow");
        applyTheme(themeTemplates.dark_yellow.colors);
        localStorage.removeItem(THEME_STORAGE_KEY);
      }
      return;
    }

    const userInfo = decodeUserInfo(token);

    // PRIORIDAD: userConfig (más reciente) > localStorage (guardado manualmente) > Token (identidad inicial)
    let targetTheme: ThemeTemplateKey | null = null;

    // 1. Si userConfig tiene tema, usarlo (es la fuente más reciente después de actualizaciones)
    if (userConfig?.theme && userConfig.theme in themeTemplates) {
      targetTheme = userConfig.theme as ThemeTemplateKey;
    }
    // 2. Si no hay userConfig, verificar localStorage (puede tener tema más reciente que el token)
    else if (!userConfig) {
      // Si userConfig es null, significa que aún está cargando
      // No cambiar el tema hasta que se cargue userConfig para evitar sobrescribir con token viejo
      // Solo aplicar en el montaje inicial
      if (isInitialMount.current) {
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && savedTheme in themeTemplates) {
          targetTheme = savedTheme as ThemeTemplateKey;
        } else if (userInfo.theme && userInfo.theme in themeTemplates) {
          targetTheme = userInfo.theme as ThemeTemplateKey;
        }
      } else {
        // En montajes posteriores, mantener el tema actual si userConfig está cargando
        return;
      }
    }
    // 3. Si userConfig existe pero no tiene tema, preferir localStorage (más reciente) antes que JWT
    else if (!userConfig.theme) {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme && savedTheme in themeTemplates) {
        targetTheme = savedTheme as ThemeTemplateKey;
      } else if (userInfo.theme && userInfo.theme in themeTemplates) {
        targetTheme = userInfo.theme as ThemeTemplateKey;
      }
    }

    // Si no se determinó un tema objetivo, mantener el actual
    if (targetTheme === null) {
      isInitialMount.current = false;
      return;
    }

    // APLICACIÓN FINAL: Comparar contra localStorage (siempre actualizado) en vez de currentTheme (puede ser stale)
    const currentSavedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (targetTheme !== currentSavedTheme || isInitialMount.current) {
      setCurrentThemeState(targetTheme);
      localStorage.setItem(THEME_STORAGE_KEY, targetTheme);
      applyTheme(themeTemplates[targetTheme].colors);

      // Sincronizar idioma también (misma prioridad)
      const lang = userConfig?.language || userInfo.language;
      if (lang) localStorage.setItem("language", lang.toLowerCase());
    }

    isInitialMount.current = false;
  }, [token, userConfig]);

  const setTheme = (theme: ThemeTemplateKey) => {
    setCurrentThemeState(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    applyTheme(themeTemplates[theme].colors);
  };

  const setTemporaryTheme = (theme: ThemeTemplateKey) => {
    setCurrentThemeState(theme);
    applyTheme(themeTemplates[theme].colors);
  };

  const contextValue = useMemo(
    () => ({
      currentTheme,
      setTheme,
      setTemporaryTheme,
    }),
    [currentTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
